// Utility to add `pageMenuItems` as `subItems` of the currently active leaf node in `menuData`.
// The active leaf node is determined by matching the current pathname
// (default: window.location.pathname).
// Returns a new menuData object with the `pageMenuItems` added as subItems to the active leaf node.
//
// - If no active path is found, adds to the first top-level node with `isActive: true`.
// - Avoids duplicate ids by checking all subItems (except those in `pageMenuItems`).
// - Does not mutate the original menuData (deep clones first).
import cloneDeep from 'lodash/cloneDeep';
import findActivePath, { excludeItems } from './findActivePath';

/**
 * Collect all subItem ids in menuData (excluding menuData.pageMenuItems).
 * Used to avoid adding duplicate ids from pageMenuItems.
 * Traverses all top-level properties of the root menuData object (except 'pageMenuItems'),
 * and collects ids from all nested subItems.
 *
 * @param {Object|Array} menuDataRoot - The menuData object or array to traverse.
 * @returns {Set<string>} Set of all subItem ids found.
 */
function collectAllSubItemIds(menuDataRoot) {
    const ids = new Set();
    function traverse(node) {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node)) {
            node.forEach(traverse);
        } else {
            // Add id if present (but not for the root obj itself)
            if (node !== menuDataRoot && node.id) {
                ids.add(node.id);
            }
            // Traverse subItems array
            if (Array.isArray(node.subItems)) {
                traverse(node.subItems);
            }
        }
    }
    // Traverse all top-level properties of menuDataRoot except 'pageMenuItems'
    Object.keys(menuDataRoot).forEach((key) => {
        if (!excludeItems.includes(key) && typeof menuDataRoot[key] === 'object') {
            traverse(menuDataRoot[key]);
        }
    });
    return ids;
}

/**
 * Merge pageMenuItems into subItems,
 * avoiding duplicates by id anywhere in menuData (excluding pageMenuItems).
 * @param {Array} subItems - The existing subItems array (may be undefined).
 * @param {Array} pageMenuItems - The pageMenuItems array to add.
 * @param {Object} menuDataObj - The full menuData object (for duplicate id check).
 * @returns {Array|undefined} The merged subItems array, or the original subItems if nothing added.
 */
function mergePageMenuItems(subItems, pageMenuItems, menuDataObj) {
    const items = Array.isArray(subItems) ? subItems.slice() : [];
    const allIds = collectAllSubItemIds(menuDataObj);
    const newItems = pageMenuItems.filter((pm) => pm && !allIds.has(pm.id));
    const concatenated = items.concat(newItems);
    return concatenated.length > 0 ? concatenated : subItems;
}

/**
 * Add pageMenuItems as subItems to the currently active leaf node in menuData.
 *
 * @param {Object} menuData - The menu structure object (with optional pageMenuItems).
 * @param {string} [pathname=window.location.pathname] - The pathname to match for active leaf.
 * @returns {Object} A new menuData object with pageMenuItems added to the active leaf node.
 */
function addPageMenuItemsToActiveLeaf(menuData, pathname = window.location.pathname) {
    // Find the path (array of ids) to the active leaf node
    const path = findActivePath(menuData, pathname);

    // If no pageMenuItems or menuData, return as is
    if (
        !menuData
        || !Array.isArray(menuData.pageMenuItems)
        || menuData.pageMenuItems.length === 0
    ) {
        return menuData;
    }

    // Deep clone menuData to avoid mutating the original
    const clone = cloneDeep(menuData);

    // If no active path found, add to the first top-level node with isActive=true
    if (!path.length) {
        let added = false;
        Object.keys(clone).forEach((key) => {
            const value = clone[key];
            if (Array.isArray(value)) {
                value.forEach((item) => {
                    if (!added && item && item.isActive) {
                        // Add pageMenuItems to this node's subItems
                         
                        item.subItems = mergePageMenuItems(
                            item.subItems,
                            clone.pageMenuItems,
                            clone,
                        );
                        added = true;
                    }
                });
            } else if (value && typeof value === 'object' && value.isActive) {
                // Add pageMenuItems to this node's subItems
                 
                value.subItems = mergePageMenuItems(value.subItems, clone.pageMenuItems, clone);
                added = true;
            }
        });
        return clone;
    }

    /**
     * Recursively traverse the menu tree to the leaf node matching the path,
     * and add pageMenuItems to its subItems.
     *
     * @param {Object|Array} node - The current node or array to traverse.
     * @param {Array} pathArr - The remaining path of ids to match.
     */
    function addSubItems(node, pathArr) {
        if (!node || !pathArr.length) return;
        const [currentId, ...rest] = pathArr;
        if (node.id === currentId) {
            if (rest.length === 0) {
                // At the leaf node: add pageMenuItems
                 
                node.subItems = mergePageMenuItems(node.subItems, clone.pageMenuItems, clone);
                return;
            }
            if (Array.isArray(node.subItems)) {
                node.subItems.forEach((child) => addSubItems(child, rest));
            }
        } else if (Array.isArray(node)) {
            node.forEach((item) => addSubItems(item, pathArr));
        }
    }

    // Traverse all top-level menu items to find the active leaf node
    Object.keys(clone).forEach((key) => {
        const value = clone[key];
        if (Array.isArray(value)) {
            value.forEach((item) => addSubItems(item, path));
        } else if (value && typeof value === 'object') {
            addSubItems(value, path);
        }
    });

    return clone;
}

export default addPageMenuItemsToActiveLeaf;
